import { Button } from "@mixitone/components";
import { ApplicationView } from "@mixitone/mvc";
import { decodeJwt } from "@mixitone/util/supabase";
import type { MouseEvent } from "react";
import { Link } from "react-router-dom";
import { Layout } from "../Components/Layout";
import { PortalController } from "../controllers/PortalController";
import { injectParams } from "../lib/injectParams";
import { isTrustedUserGesture } from "../lib/isTrustedUserGesture";
import { createSupabase } from "../supabaseClient";

/**
 * Landing page for email magic links. The OTP in the URL is single-use, so we
 * do not redeem it on load. Email scanners (Outlook Safe Links, etc.) fetch
 * the URL and run page JS; requiring a trusted click keeps the token valid
 * for the actual recipient.
 */

interface State {
  loading: boolean;
  expired: boolean;
  success: boolean;
  token: string;
}
interface Props {
  params: { token: string };
}

class MagicLinkPageController extends PortalController<State, Props> {
  get initialState(): State {
    return {
      loading: false,
      expired: false,
      success: false,
      token: "",
    };
  }

  async initialize(props: Props) {
    this.state.token = props.params.token;
    try {
      const jwt = decodeJwt(props.params.token);
      if (typeof jwt.exp === "number" && jwt.exp * 1000 < Date.now()) {
        this.state.expired = true;
      }
    } catch {
      this.state.expired = true;
    }
  }

  async actionContinue() {
    if (this.state.loading || this.state.expired || this.state.success) return;

    this.state.loading = true;

    const supabase = createSupabase();
    const response = await supabase.functions.invoke("player-portal", {
      body: JSON.stringify({
        action: "signInWithToken",
        token: this.state.token,
      }),
    });
    if (response.error) {
      console.error(response.error);
      this.state.expired = true;
    } else if (!response.data?.access_token) {
      console.error("No access token found in response");
      this.state.expired = true;
    } else {
      const { access_token, refresh_token } = response.data;

      const session = await supabase.auth.setSession({
        access_token,
        refresh_token,
      });
      if (session.error) {
        console.error(session.error);
        this.state.expired = true;
      } else {
        this.state.success = true;
        this.redirectAfterSignIn(this.state.token);
      }
    }

    this.state.loading = false;
  }

  redirectAfterSignIn(token: string) {
    const jwt = decodeJwt(token);
    if (jwt && "redirectTo" in jwt) {
      this.navigate(jwt.redirectTo as string);
    } else {
      this.navigate("/in/home");
    }
  }
}

const ControlledMagicLinkPage = () => {
  const controller = MagicLinkPageController.use();
  const { loading, expired } = controller.state;

  const handleContinue = (event: MouseEvent<HTMLButtonElement>) => {
    if (!isTrustedUserGesture(event)) return;
    controller.actionContinue();
  };

  if (expired) {
    return (
      <Layout title="Sign in">
        <p>
          Your link has expired. <Link to="/">Request a new one</Link>
        </p>
      </Layout>
    );
  }

  return (
    <Layout title="Sign in">
      <p className="mb-4 text-sm text-gray-500">This email link will sign you in to the player portal.</p>
      <Button type="button" className="w-full" onClick={handleContinue} disabled={loading} spinner={loading}>
        Continue
      </Button>
    </Layout>
  );
};

export const MagicLinkPage = injectParams(
  MagicLinkPageController.scope(ApplicationView(ControlledMagicLinkPage)),
);
